<template>
  <!-- fullstory: fs-mask keeps profile pii (name / email / phone) out of recordings -->
  <div style="margin-top:20px" class="fs-mask">

    <!-- page banner: matches the Fleet Assistant / other tenant-config pages
         (soft gray fill + dotted border + bold title + italic subtitle) so
         this page reads as part of the same settings family. -->
    <div class="ud-banner">
      <span class="ud-banner-title">User Profile</span><br/>
      <div class="ud-banner-sub">Manage account info and Fleet Integrations preferences</div>
    </div>

    <div v-if="model" style="text-align: left; color: #666;" class="d-flex align-items-center justify-content-center">
      <form @submit.prevent="saveUser">
        <table class="ud-form-table" cellpadding="10px">

          <!-- ACCOUNT -->
          <tr>
            <td colspan="3" class="ud-section-cell">
              <span class="ud-section-title">Account</span>
              <hr class="ud-section-rule"/>
            </td>
          </tr>
          <tr>
            <td align="right" class="ud-label">Username</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.username" id="create-user-email" disabled/>
            </td>
            <td class="ud-help">Your login email. Not editable here — contact support to change.</td>
          </tr>
          <tr>
            <td align="right" class="ud-label">Password</td>
            <td class="ud-control">
              <b-input-group>
                <b-form-input type="text" v-model="model.password" id="create-user-pwd" autocomplete="off" placeholder="Leave blank to keep current password" class="fs-exclude"/>
                <b-input-group-append style="margin-left: 8px;">
                  <b-button variant="secondary" @click.prevent="generatePassword" id="generate-password-btn">Generate</b-button>
                </b-input-group-append>
              </b-input-group>
              <div v-if="passwordGenerated" style="margin-top: 6px;">
                <b-form-checkbox v-model="sendPasswordEmail" id="send-password-email-checkbox">
                  Email new password to user
                </b-form-checkbox>
              </div>
            </td>
            <td class="ud-help">Only fill this in if you want to change the password.</td>
          </tr>

          <!-- PASSKEYS (FI-2182, staff preview): right under the account
               credentials it belongs with. your own passkeys on your own
               profile, remove-only on anyone else's (a lost device). the
               section owns its rows and its api calls; alerts come back up
               through forwardAlert. -->
          <passkeys-section v-if="showPasskeys" :username="model.username" :mode="isEditingSelf ? 'self' : 'admin'" :target-role="model.role" @showAlert="forwardAlert" />

          <!-- PERSONAL -->
          <tr>
            <td colspan="3" class="ud-section-cell">
              <span class="ud-section-title">Personal</span>
              <hr class="ud-section-rule"/>
            </td>
          </tr>
          <tr>
            <td align="right" class="ud-label">First Name</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.first_name" id="create-user-first-name"/>
            </td>
            <td class="ud-help">&nbsp;</td>
          </tr>
          <tr>
            <td align="right" class="ud-label">Last Name</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.last_name" id="create-user-last-name"/>
            </td>
            <td class="ud-help">&nbsp;</td>
          </tr>
          <tr>
            <td align="right" class="ud-label">Phone Number</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.phone_number" id="create-user-phone"/>
            </td>
            <td class="ud-help">Used for SMS alerts and on-call contact.</td>
          </tr>
          <tr>
            <td align="right" class="ud-label">Job Title</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.job_title" id="create-user-job-title"/>
            </td>
            <td class="ud-help">&nbsp;</td>
          </tr>
          <tr>
            <td align="right" class="ud-label">Company / Location</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.company_location" id="create-user-company-location"/>
            </td>
            <td class="ud-help">&nbsp;</td>
          </tr>
          <tr>
            <td align="right" class="ud-label">Technical Contact</td>
            <td class="ud-control">
              <b-form-checkbox v-model="model.technical_contact" id="ud-technical-contact">Designate this user as a technical contact</b-form-checkbox>
            </td>
            <td class="ud-help">&nbsp;</td>
          </tr>

          <!-- EMPLOYMENT (dattco only) -->
          <template v-if="this.user.tenant == 'dattco-413f464d'">
            <tr>
              <td colspan="3" class="ud-section-cell">
                <span class="ud-section-title">Employment</span>
                <hr class="ud-section-rule"/>
              </td>
            </tr>
            <tr>
              <td align="right" class="ud-label">Employment Status</td>
              <td class="ud-control">
                <b-form-select v-model="model.employment_status" :options="employmentStatusOptions" id="create-user-employment_status"/>
              </td>
              <td class="ud-help">Full-time, part-time, or casual. Drives payroll reports.</td>
            </tr>
            <tr>
              <td align="right" class="ud-label">TBN Driver ID</td>
              <td class="ud-control">
                <b-form-input type="text" v-model="model.tbn_driver_id" id="create-user-tbn_driver_id"/>
              </td>
              <td class="ud-help">Links this user to their driver record in TBN.</td>
            </tr>
          </template>

          <!-- ROLE & ACCESS -->
          <tr>
            <td colspan="3" class="ud-section-cell">
              <span class="ud-section-title">Role &amp; Access</span>
              <hr class="ud-section-rule"/>
            </td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Role</td>
            <td class="ud-control">
              <!-- role tag: icon + uppercase label pill showing the current
                   role. static here — the preview-as-customer toggle lives
                   on the matching tag in the navbar profile popup so the
                   admin can switch views from anywhere in the app. -->
              <div class="ud-role-tag-row">
                <span class="role-tag" :class="'role-tag-' + (model.role || 'other')">
                  <i class="fa" :class="roleIconFor(model.role)" aria-hidden="true"></i>
                  <span>{{ roleDisplayLabel(model.role) }}</span>
                </span>
              </div>
              <!-- three layers of protection against accidental self-demotion:
                     1. disabled whenever isEditingSelf (covers the "admin saves
                        their own profile and the dropdown silently swaps
                        'admin' -> 'customer-admin'" footgun)
                     2. disabled for non-admins so general staff can't promote
                     3. roleOptionsWithCurrent injects a read-only ghost entry
                        for whatever the loaded role actually is, so a sys-admin
                        editing their own profile can still see "System Admin"
                        in the (disabled) dropdown instead of a blank select. -->
              <b-form-select
                v-model="model.role"
                :options="roleOptionsWithCurrent"
                id="create-user-role"
                :disabled="isEditingSelf || (this.user.role != 'admin' && this.user.role != 'customer-admin')"
              />
            </td>
            <td valign="top" class="ud-help">
              <span v-if="isEditingSelf">Another admin has to change your role — this field is read-only on your own profile to prevent accidental lockout.</span>
              <span v-else>Controls which features and pages are available. Only admins can change this.</span>
            </td>
          </tr>
          <tr>
            <td align="right" class="ud-label">Tags</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.tags" id="create-user-tags"/>
            </td>
            <td class="ud-help">Optional comma-separated labels used for grouping and filtering.</td>
          </tr>
          <tr v-if="model.role === 'booking-customer'">
            <td align="right" class="ud-label">Primary Contact Organization</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.primary_contact_organization" id="create-user-primary-contact-org" placeholder="Must match TBN booking PrimaryContactOrganization"/>
            </td>
            <td class="ud-help">Comma-separate to link this user to multiple organizations.</td>
          </tr>
          <tr v-if="model.role === 'booking-customer'">
            <td align="right" class="ud-label">Home Page</td>
            <td class="ud-control">
              <b-form-input type="text" v-model="model.home_page" id="create-user-home-page" placeholder="/booking-customer"/>
            </td>
            <td class="ud-help">Where this user lands after signing in.</td>
          </tr>

          <!-- EMAIL NOTIFICATIONS -->
          <!-- four coarse-grained opt-ins layered on top of the existing
               per-report "email me when ready" toggles in the Reports UI:
                 - email_service_alerts covers outages / service-impacting
                   issues (Samsara degraded, SMS delivery broken, etc).
                   defaults ON because these are low-volume and actionable —
                   a silent default would mean users miss outages.
                 - email_report_ready acts as a master switch. when off,
                   per-report opt-ins are suppressed; when on, they still
                   apply for fine-grained control.
                 - email_announcements is net-new delivery (announcements
                   previously only showed in the Notification Center).
                 - email_other is a catch-all for future notification
                   types so we don't need to ship another pref for each
                   new category. -->
          <tr>
            <td colspan="3" class="ud-section-cell">
              <span class="ud-section-title">Email Notifications</span>
              <hr class="ud-section-rule"/>
            </td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Service alerts</td>
            <td valign="top" class="ud-control">
              <b-form-checkbox v-model="emailServiceAlerts">Email me when a service is degraded or an outage is detected</b-form-checkbox>
            </td>
            <td valign="top" class="ud-help">Outages and service-impacting issues — e.g. a telematics provider is down or SMS delivery is degraded. Low volume, high-priority; on by default.</td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Announcements</td>
            <td valign="top" class="ud-control">
              <b-form-checkbox v-model="emailAnnouncements">Email me when an announcement is posted</b-form-checkbox>
            </td>
            <td valign="top" class="ud-help">Product updates, maintenance windows, and other broadcast messages. On by default. Unchecking stops the emails; you will still see announcements in your Notification Center.</td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Support tickets</td>
            <td valign="top" class="ud-control">
              <b-form-checkbox v-model="emailSupportTickets">Email me when someone comments on a support ticket I'm on</b-form-checkbox>
            </td>
            <td valign="top" class="ud-help">Includes tickets you opened, are assigned to, or have commented on. On by default so you don't miss replies.</td>
          </tr>
          <!-- FI-1935: the ops desk email pref existed server-side (every ERM
               email checks it) but had no switch here until assignment mail
               made it worth one. -->
          <tr>
            <td align="right" valign="top" class="ud-label">Event Response Manager</td>
            <td valign="top" class="ud-control">
              <b-form-checkbox v-model="emailOpsDesk">Email me about Event Response Manager events</b-form-checkbox>
            </td>
            <td valign="top" class="ud-help">New events and comments sent to your fleet's default recipients, status updates on events you filed, events assigned to you, and shout-outs when a kudos names you. Notification Center cards arrive either way.</td>
          </tr>
          <!-- FI-2097: subject ordering for ERM event emails. a value pick,
               not an opt-in toggle like its neighbors; unset follows the
               company default from the ERM notification settings. -->
          <tr>
            <td align="right" valign="top" class="ud-label">ERM email subject</td>
            <td valign="top" class="ud-control">
              <b-form-select v-model="ermSubjectFormat" :options="ermSubjectFormatOptions" id="ud-erm-subject-format"/>
            </td>
            <td valign="top" class="ud-help">How Event Response Manager email subjects are ordered. Ticket number first keeps the event number where email threads group on it; event details first leads with the category, vehicle, and driver so an inbox scan reads the facts.</td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Reports ready</td>
            <td valign="top" class="ud-control">
              <b-form-checkbox v-model="emailReportReady">Email me when one of my reports finishes</b-form-checkbox>
            </td>
            <td valign="top" class="ud-help">Applies to every report you generate. You can still opt out of individual reports from the Reports page.</td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Other</td>
            <td valign="top" class="ud-control">
              <b-form-checkbox v-model="emailOther">Email me for other notifications</b-form-checkbox>
            </td>
            <td valign="top" class="ud-help">Everything else delivered to your Notification Center — account alerts, system messages, and new categories added in the future.</td>
          </tr>

          <!-- FLEET ASSISTANT -->
          <tr>
            <td colspan="3" class="ud-section-cell">
              <span class="ud-section-title">Fleet Assistant</span>
              <hr class="ud-section-rule"/>
            </td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Access</td>
            <td valign="top" class="ud-control">
              <b-form-checkbox
                v-model="fleetAssistantAccess"
                id="ud-fa-access"
                :disabled="this.user.role != 'admin' && this.user.role != 'customer-admin'"
              >Allow this user to use Fleet Assistant</b-form-checkbox>
            </td>
            <td valign="top" class="ud-help">Fleet Assistant can read driver and customer details, so it's granted per user. Only admins can change this, and the tenant must have Fleet Assistant enabled. Takes effect the next time the user signs in.</td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Launcher</td>
            <td valign="top" class="ud-control">
              <b-form-select v-model="faLauncherVisibility" :options="faLauncherVisibilityOptions"/>
            </td>
            <td valign="top" class="ud-help">When hidden, the floating Fleet Assistant button won't appear on any page. You can still open it from the Reports menu.</td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Icon</td>
            <td valign="top" class="ud-control">
              <b-form-select v-model="faIcon" :options="faIconOptions"/>
            </td>
            <td valign="top" class="ud-help">The face of the Fleet Assistant. Each icon idles quietly and plays a livelier animation when the assistant has something for you. Pick one to see it previewed below.</td>
          </tr>
          <!-- icon preview: not rendered at all until the user picks an icon,
               so the profile page isn't animating at someone who came here to
               change something else. once shown it idles in place, and
               clicking it replays the attention animation. -->
          <tr v-if="faIconPreviewAwake">
            <td align="right" valign="top" class="ud-label">Preview</td>
            <td valign="top" class="ud-control">
              <div class="ud-preview-stage ud-preview-stage--avatar">
                <button
                  type="button"
                  class="ud-avatar-demo-btn"
                  :title="faIconDemoActive ? 'Previewing the attention animation' : 'Click to preview the attention animation'"
                  aria-label="Preview the attention animation"
                  @click="previewFaIcon"
                >
                  <fa_avatar class="ud-avatar-demo-svg" :variant="faIcon" :active="faIconDemoActive"/>
                </button>
              </div>
            </td>
            <td valign="top" class="ud-help">Live preview. Click the icon to see how it grabs your attention when a reply is waiting.</td>
          </tr>

          <!-- TUTORIAL PREFERENCES -->
          <tr>
            <td colspan="3" class="ud-section-cell">
              <span class="ud-section-title">Tutorial Preferences</span>
              <hr class="ud-section-rule"/>
            </td>
          </tr>
          <!-- both tutorial dropdowns stack above a single combined preview:
               the textbox types out at the chosen animation speed while its
               own border carries the chosen highlight style, so the two
               settings are demoed together on the same surface. -->
          <tr>
            <td align="right" valign="top" class="ud-label">Animation speed</td>
            <td valign="top" class="ud-control">
              <b-form-select v-model="faTutorialMode" :options="faTutorialModeOptions"/>
            </td>
            <td valign="top" class="ud-help">How fast tutorials will go. Change it to see it previewed below.</td>
          </tr>
          <tr>
            <td align="right" valign="top" class="ud-label">Highlight style</td>
            <td valign="top" class="ud-control">
              <b-form-select v-model="highlightStyle" :options="highlightStyleOptions"/>
            </td>
            <td valign="top" class="ud-help">Which animation surrounds the control the tutorial is pointing at. Change it to see it previewed below.</td>
          </tr>
          <!-- preview row uses the standard 3-column layout (label | stage |
               help) so it sits next to Animation speed / Highlight style
               on desktop. on mobile the shared ud-form-table grid CSS
               collapses it the same way it collapses the rows above:
               label stretched across the top, stage in col 2 row 1,
               help in col 2 row 2. -->
          <tr v-if="tutorialPreviewAwake">
            <td align="right" valign="top" class="ud-label">Preview</td>
            <td valign="top" class="ud-control">
              <div class="ud-preview-stage ud-preview-stage--typing">
                <div class="ud-preview-input" :class="['ud-preview-input--' + highlightStyle, highlightStyle === 'aurora-v2' ? 'fi-halo-aurora-v2' : '']">
                  <!-- inner wrapper clips long text on the right; text + caret
                       flow inline so the caret sits immediately after the
                       most-recently-typed character rather than at the row end. -->
                  <div class="ud-preview-input-inner">
                    <span class="ud-preview-text">{{ previewText }}</span><span class="ud-preview-caret" :class="{ 'ud-preview-caret--off': faTutorialMode === 'off' }"></span>
                  </div>
                </div>
              </div>
            </td>
            <td valign="top" class="ud-help">Live preview of both settings together — the tutorial types out your chosen message and highlights the input it's pointing at.</td>
          </tr>

          <!-- SAVE / CANCEL -->
          <tr>
            <td colspan="3" class="ud-actions-cell">
              <b-btn type="submit" variant="success" id="create-user-submit">Save</b-btn>
              &nbsp;&nbsp;
              <b-btn v-if="(user.role == 'admin' || user.role == 'customer-admin')" :to="{ name: 'user_list'}">Cancel</b-btn>
            </td>
          </tr>
        </table>
      </form>
    </div>
  </div>
</template>

<script>

import {get_user, update_user} from '../networking/apis'
import {mapMutations, mapState} from "vuex";
import fa_avatar from './fa_avatar.vue'
import passkeys_section from './passkeys_section.vue'
import { canManagePasskeys, passkeysFlagOn } from '../utils/passkeys'

// sample message the tutorial preview types out. shown in full, statically,
// until the user actually touches one of the tutorial dropdowns.
const PREVIEW_SAMPLE_TEXT = 'How can I improve on-time performance?'

export default {
  name: 'user_detail',
  components: { fa_avatar, 'passkeys-section': passkeys_section },
  data () {
    return {
      model: {},
      passwordGenerated: false,
      sendPasswordEmail: true,
      // captured when the user record first loads. used as the source of truth
      // for the `role` field on save when editing yourself, so even if the
      // dropdown gets bypassed somehow (future refactor, DevTools, extension),
      // the user can't accidentally change their own role here.
      originalRole: null,
      // short dropdown labels — the live preview row demonstrates what each
      // choice actually looks like, so the label itself can stay terse.
      faLauncherVisibilityOptions: [
        { value: 'show', text: 'Show (default)' },
        { value: 'hide', text: 'Hidden' },
      ],
      faTutorialModeOptions: [
        { value: 'slow', text: 'Slow' },
        { value: 'full', text: 'Full (default)' },
        { value: 'fast', text: 'Fast' },
        { value: 'off',  text: 'Instant' },
      ],
      highlightStyleOptions: [
        { value: 'pulse',   text: 'Pulse (default)' },
        { value: 'chase',   text: 'Prism' },
        { value: 'ripple',  text: 'Ripple' },
        { value: 'magenta', text: 'Magenta' },
        { value: 'aurora',  text: 'Aurora' },
        { value: 'aurora-v2', text: 'Aurora v2' },
      ],
      faIconOptions: [
        { value: 'bubble',  text: 'Chat Bubble (default)' },
        { value: 'cloud',   text: 'Sync Cloud' },
        { value: 'stapler', text: 'Stapler Bus' },
        { value: 'nova',    text: 'Nova' },
        { value: 'plasma',  text: 'Plasma' },
      ],
      // true while the icon preview is demoing its attention animation
      faIconDemoActive: false,
      faIconDemoTimer: null,
      // both preview rows are hidden until their setting is changed — a page
      // full of looping animation reads as noise when you opened the profile
      // to change something else. the avatar preview appears when an icon is
      // picked, the tutorial preview when either tutorial dropdown changes.
      // once shown they stay up for the rest of the visit so repeat tweaks
      // demo immediately.
      faIconPreviewAwake: false,
      tutorialPreviewAwake: false,
      // live preview state. driven by startPreviewLoop() — types the demo
      // text at a pace derived from faTutorialMode, then pauses, then resets.
      previewText: '',
      previewTimer: null,
      roleOptions: [
        { value: 'customer-admin', text: 'Fleet Admin' },
        { value: 'customer', text: 'Staff' },
        { value: 'booking-customer', text: 'Booking Customer' },
        { value: 'driver', text: 'Driver' },
        { value: 'customer-care', text: 'Customer Care' },
        { value: 'manager', text: 'Manager' },
        { value: 'dispatch', text: 'Dispatch' },
        { value: 'garage', text: 'Garage' },
        { value: 'wash-bay', text: 'Wash Bay' },
        { value: 'other', text: 'Other' },
      ],
      employmentStatusOptions: [
        {value: 'full', text: 'Full Time'},
        {value: 'part', text: 'Part Time'},
        {value: 'casual', text: 'Casual'}
      ],
      // FI-2097 subject ordering for erm event emails; '' follows the
      // company default set in the erm notification settings
      ermSubjectFormatOptions: [
        { value: '', text: 'Company default' },
        { value: 'default', text: 'Ticket number first' },
        { value: 'readable', text: 'Event details first' },
      ]
    }
  },
  async created() {
    await this.load_data()
  },
  beforeDestroy() {
    this.stopPreviewLoop()
    if (this.faIconDemoTimer) {
      clearTimeout(this.faIconDemoTimer)
      this.faIconDemoTimer = null
    }
  },
  methods: {
    generatePassword() {
      const length = 12
      const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
      let pwd = ''
      for (let i = 0; i < length; i++) {
        pwd += charset.charAt(Math.floor(Math.random() * charset.length))
      }
      this.$set(this.model, 'password', pwd)
      this.passwordGenerated = true
      this.sendPasswordEmail = true
    },
    // --- role tag helpers ---
    // software/fleet-flavoured icons for each role. unknown roles fall back
    // to a neutral circle. MUGGLE is handled inline in the template because
    // it's a toggle state, not a persisted role.
    roleIconFor(role) {
      const icons = {
        admin:               'fa-shield',
        'customer-admin':    'fa-cogs',
        customer:            'fa-user',
        'booking-customer':  'fa-calendar-check-o',
        driver:              'fa-id-badge',
        'customer-care':     'fa-headphones',
        manager:             'fa-briefcase',
        dispatch:            'fa-compass',
        garage:              'fa-wrench',
        'wash-bay':          'fa-tint',
        other:               'fa-circle-o',
      }
      return icons[role] || icons.other
    },
    // uppercase tag labels. mirrors the roleOptions.text values where
    // reasonable and renders 'admin' as the sys-admin's SYS ADMIN.
    // customer-admin displays as "FLEET ADMIN" — never
    // bare "ADMIN" on a surface a customer can see, because that term
    // belongs to the internal Initek role taxonomy.
    roleDisplayLabel(role) {
      const labels = {
        admin:               'SYS ADMIN',
        'customer-admin':    'FLEET ADMIN',
        customer:            'STAFF',
        'booking-customer':  'BOOKING CUSTOMER',
        driver:              'DRIVER',
        'customer-care':     'CUSTOMER CARE',
        manager:             'MANAGER',
        dispatch:            'DISPATCH',
        garage:              'GARAGE',
        'wash-bay':          'WASH BAY',
        other:               'OTHER',
      }
      return labels[role] || (role ? role.toUpperCase() : 'UNKNOWN')
    },
    // --- live preview loop ---
    // drives the typing preview: types the sample message one character at a
    // time with a small random jitter, pauses at the end, then clears and
    // loops. pacing parameters mirror the real typeMessage() implementation
    // in fleet_assistant.vue so what the user sees here matches what they
    // get during a real tutorial.
    startPreviewLoop() {
      this.stopPreviewLoop()
      const text = PREVIEW_SAMPLE_TEXT
      const mode = this.faTutorialMode
      if (mode === 'off') {
        // "off" drops the message in instantly — no typing animation. keep it
        // shown for a beat, then reset so the loop still demonstrates the
        // non-animated drop-in behavior on repeat.
        this.previewText = text
        this.previewTimer = setTimeout(() => {
          this.previewText = ''
          this.previewTimer = setTimeout(() => this.startPreviewLoop(), 400)
        }, 1800)
        return
      }
      let baseDelay, jitter, holdMs
      if (mode === 'slow')      { baseDelay = 70; jitter = 35; holdMs = 1400 }
      else if (mode === 'fast') { baseDelay = 12; jitter = 10; holdMs = 500 }
      else                      { baseDelay = 38; jitter = 22; holdMs = 900 }

      this.previewText = ''
      let i = 0
      const typeNext = () => {
        if (i < text.length) {
          this.previewText = text.slice(0, i + 1)
          i++
          this.previewTimer = setTimeout(typeNext, baseDelay + Math.floor(Math.random() * jitter))
        } else {
          this.previewTimer = setTimeout(() => {
            this.previewText = ''
            this.previewTimer = setTimeout(() => this.startPreviewLoop(), 300)
          }, holdMs)
        }
      }
      typeNext()
    },
    stopPreviewLoop() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer)
        this.previewTimer = null
      }
    },
    // reveal the icon preview (first time) and play its attention animation
    // for a few seconds, then settle back to idle. re-clicks restart the
    // window. reached from the faIcon setter and from a click on the preview
    // itself, so the row only ever appears on a deliberate icon change.
    previewFaIcon() {
      this.faIconPreviewAwake = true
      this.faIconDemoActive = true
      if (this.faIconDemoTimer) clearTimeout(this.faIconDemoTimer)
      this.faIconDemoTimer = setTimeout(() => {
        this.faIconDemoActive = false
        this.faIconDemoTimer = null
      }, 3200)
    },
    // the passkeys section reports through the same showAlert the page uses
    forwardAlert(message, variant, timer) {
      this.$emit('showAlert', message, variant, timer)
    },
    async load_data() {
      try {
        // the /preferences route (linked from email footers) carries no
        // ?username, so fall back to the signed-in user. App.vue calls
        // set_user_data from localStorage before child created() hooks run,
        // so this.user is populated by the time we get here.
        const username = this.$route.query.username || (this.user && this.user.username)
        if (username) {
          const {value: {data: user_data = []}} = (await get_user(this.user.tenant, username))
          this.model = user_data
          // the API never returns the stored password hash, but the field is
          // bound with v-model — declare it up front so it's reactive (a key
          // added later by generatePassword wouldn't re-render the input) and
          // so it starts blank, meaning "keep the current password".
          this.$set(this.model, 'password', '')
          // remember the backend-truth role. used on save to guarantee a
          // self-edit never changes the editor's own role. see saveUser.
          this.originalRole = user_data && user_data.role ? user_data.role : null
        }
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    async saveUser () {
      try{
        // hard guarantee: a self-edit can never change the editor's own role.
        // the UI disables the dropdown and filters the options, but this is
        // the last line of defense against any v-model drift or future bug
        // that lets a bad role value slip into the payload.
        if (this.isEditingSelf && this.originalRole) {
          this.model.role = this.originalRole
        }
        this.$emit('showAlert', "waiting for response...", 'info')
        const payload = { ...this.model, send_password_email: this.passwordGenerated && this.sendPasswordEmail }
        // a blank field means "don't change it" — drop the key so the backend
        // keeps whatever hash is already stored instead of hashing an empty string
        if (!payload.password || !payload.password.trim()) {
          delete payload.password
        }
        const { message, status_code } = await update_user(this.user.tenant, payload)
        this.$emit('showAlert', message || 'Success!', 'success', 1500)
        this.passwordGenerated = false
        this.sendPasswordEmail = true
        // propagate preferences into Vuex so the launcher / FA pick up the new
        // tutorial mode without requiring a re-login. only applies when editing
        // your own profile (comparing username is the simplest safe check).
        if (this.model && this.model.username === this.user.username) {
          const merged = { ...this.user, preferences: { ...(this.model.preferences || {}) } }
          this.set_user_data(merged)
          try {
            localStorage.setItem('user', JSON.stringify(merged))
          } catch (_) {}
        }
        if (this.user.role == 'admin' || this.user.role == 'customer_admin')
          this.$router.push({name:'user_list'})
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    ...mapMutations('profile', ['set_user_data']),
  },
  computed:{
    ...mapState({
      user: state => state.profile.user,
    }),
    // true when the form is loaded with your own user record. gates every
    // role-safety branch below.
    isEditingSelf() {
      return !!(this.model && this.user && this.model.username && this.model.username === this.user.username)
    },
    // FI-2182: the viewer must be staff for both modes while the api
    // allow-list is admin only. reads the real vuex role, so a preview-as
    // customer toggle cannot widen it, and waits for the loaded model so
    // the section never mounts with an empty username. the build flag alone
    // gates the mount, not the browser: removing a lost device's passkey
    // from another account needs no authenticator, and the section says so
    // itself when this browser cannot add one.
    showPasskeys() {
      return passkeysFlagOn() && canManagePasskeys(this.user) && !!(this.model && this.model.username)
    },
    // roleOptions with a disabled ghost entry injected for the loaded role if
    // it isn't already in the list. handles the 'admin' (sys-admin) role which
    // is intentionally NOT a selectable option for anyone — but we still want
    // to show it in the disabled dropdown so a sys-admin can see "System Admin"
    // displayed rather than a blank field.
    roleOptionsWithCurrent() {
      const current = this.model && this.model.role
      if (!current) return this.roleOptions
      if (this.roleOptions.some(o => o.value === current)) return this.roleOptions
      const prettyLabels = { admin: 'System Admin' }
      const label = prettyLabels[current] || current
      return [{ value: current, text: label, disabled: true }, ...this.roleOptions]
    },
    // admin-granted privilege stored as a top-level user field (not a
    // preference, so a user can't self-set it). the server is authoritative;
    // this just drives the gated checkbox. payload spreads model on save.
    fleetAssistantAccess: {
      get() {
        return !!(this.model && this.model.fleet_assistant_access)
      },
      set(val) {
        if (!this.model) return
        this.$set(this.model, 'fleet_assistant_access', !!val)
      },
    },
    // two-way bind into model.preferences.fa_tutorial_mode. lazy-inits the
    // preferences map the first time the user picks a value so we only send
    // preferences to the backend when the user actually sets one.
    faTutorialMode: {
      get() {
        const p = this.model && this.model.preferences
        return (p && p.fa_tutorial_mode) || 'full'
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'fa_tutorial_mode', val)
      },
    },
    // which avatar the fleet assistant wears (launcher pill + panel header).
    // same lazy-init pattern as the other preferences below.
    faIcon: {
      get() {
        const p = this.model && this.model.preferences
        const v = p && p.fa_icon
        return (v === 'cloud' || v === 'stapler' || v === 'nova' || v === 'plasma') ? v : 'bubble'
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'fa_icon', val)
        // demo the attention animation right away so the pick is a choice,
        // not a guess
        this.previewFaIcon()
      },
    },
    // companion to fa_tutorial_mode: controls which highlight animation plays
    // on the launcher during the tutorial. defaults to 'pulse' (legacy behavior)
    // so existing users see no change until they opt in.
    highlightStyle: {
      get() {
        const p = this.model && this.model.preferences
        return (p && p.highlight_style) || 'pulse'
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'highlight_style', val)
      },
    },
    // hide the floating FA launcher entirely for users who don't want it
    // taking up screen real estate. stored as a 'show'/'hide' string to
    // match the other dropdown-driven prefs; App.vue reads the raw boolean
    // flag on preferences.hide_fleet_assistant_launcher.
    faLauncherVisibility: {
      get() {
        const p = this.model && this.model.preferences
        return p && p.hide_fleet_assistant_launcher ? 'hide' : 'show'
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'hide_fleet_assistant_launcher', val === 'hide')
      },
    },
    // email-notification prefs — coarse-grained profile-level opt-ins
    // checked server-side in notification_service._send_profile_pref_email
    // and _send_email_if_opted_in. announcements/reports/other default
    // off for existing users (opt-in). service alerts default ON —
    // absence of the key is treated as true, only an explicit false
    // suppresses. these are low-volume outage notifications and defaulting
    // off would mean users miss "samsara is down" at exactly the wrong time.
    emailServiceAlerts: {
      get() {
        const p = this.model && this.model.preferences
        if (!p || p.email_service_alerts === undefined) return true
        return !!p.email_service_alerts
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'email_service_alerts', !!val)
      },
    },
    emailAnnouncements: {
      // FI-2040: on by default, matching _announcements_pref_on on the
      // server. if these two disagreed the checkbox would lie about what
      // the backend is going to do.
      get() {
        const p = this.model && this.model.preferences
        if (!p || p.email_announcements === undefined) return true
        return !!p.email_announcements
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'email_announcements', !!val)
      },
    },
    // FI-1642: support-ticket comments default ON, same default-true
    // semantics as service_alerts. if you're on a ticket you almost
    // always want to know when someone replies.
    emailSupportTickets: {
      get() {
        const p = this.model && this.model.preferences
        if (!p || p.email_support_tickets === undefined) return true
        return !!p.email_support_tickets
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'email_support_tickets', !!val)
      },
    },
    // FI-1935: same shape and default as the support-ticket pref; the api
    // reads it as PREF_EMAIL_OPS_DESK on every ops desk email
    emailOpsDesk: {
      get() {
        const p = this.model && this.model.preferences
        if (!p || p.email_ops_desk === undefined) return true
        return !!p.email_ops_desk
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'email_ops_desk', !!val)
      },
    },
    // FI-2097: subject ordering for erm event emails. '' = follow the
    // company default (the erm notification settings pick), so only an
    // explicit choice is stored. same lazy-init pattern as the toggles.
    ermSubjectFormat: {
      get() {
        const p = this.model && this.model.preferences
        const v = p && p.ops_desk_subject_format
        return v === 'default' || v === 'readable' ? v : ''
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'ops_desk_subject_format', val || '')
      },
    },
    emailReportReady: {
      get() {
        const p = this.model && this.model.preferences
        return !!(p && p.email_report_ready)
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'email_report_ready', !!val)
      },
    },
    emailOther: {
      get() {
        const p = this.model && this.model.preferences
        return !!(p && p.email_other)
      },
      set(val) {
        if (!this.model.preferences) {
          this.$set(this.model, 'preferences', {})
        }
        this.$set(this.model.preferences, 'email_other', !!val)
      },
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    },
    // reload when the route query changes — handles the case where the user
    // is already on a user_detail page and clicks the profile button, which
    // updates ?username= without remounting the component.
    '$route.query.username'() {
      this.load_data()
    },
    // changing either tutorial setting reveals the preview row and (re)starts
    // the typing demo, so the user sees the new cadence / highlight right
    // after picking it. before that first change there's no preview at all.
    faTutorialMode() {
      this.tutorialPreviewAwake = true
      this.startPreviewLoop()
    },
    highlightStyle() {
      this.tutorialPreviewAwake = true
      this.startPreviewLoop()
    }
  }
}
</script>

<style scoped>
/* banner / table / section styling mirrors tenant_config_fleet_assistant so
   the settings pages feel like one family (soft gray+dotted banner, steelblue
   section headers, 3-column label/control/help rows, small muted help text). */

.ud-banner {
  width: 100%;
  background-color: #eee;
  border: 1px dotted grey;
  margin-bottom: 25px;
  padding-left: 4px;
}
.ud-banner-title {
  font-size: 26px;
  font-weight: bold;
}
.ud-banner-sub {
  font-style: italic;
  font-size: 16px;
  padding-bottom: 10px;
  margin-right: 10px;
}

/* table is only visually a layout grid — bold is applied selectively to the
   left-column labels and section titles rather than cascading from the table,
   so the right-side controls (radio labels, inputs, help text) stay normal. */
.ud-form-table {
  width: 90%;
  max-width: 1250px;
  font-size: 18px;
}

.ud-section-cell {
  padding-top: 20px;
}
.ud-section-title {
  color: steelblue;
  font-weight: bold;
  font-size: 20px;
}
.ud-section-rule {
  margin: 5px 0 15px 0;
}

.ud-label {
  width: 280px;
  font-weight: bold;
  white-space: nowrap;
}
.ud-control {
  width: 460px;
  font-weight: normal;
}
.ud-help {
  width: 320px;
  font-size: 12px;
  color: #888;
  font-weight: normal;
}

/* role tag row — sits above the role dropdown on the user-profile form. */
.ud-role-tag-row {
  margin-bottom: 8px;
}

/* base role tag styling — icon + uppercase label pill. variants override
   just the colour pair below. the interactive variant (admin/muggle) gets
   a button reset + hover/focus affordance so it reads as clickable. */
.role-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  min-height: 22px;
  font-size: 12px;
  line-height: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  border-radius: 4px;
  border: 1px solid var(--role-tag-color, #6c757d);
  background: var(--role-tag-bg, #e9ecef);
  color: var(--role-tag-fg, #333);
  white-space: nowrap;
}
.role-tag .fa {
  font-size: 12px;
}
.role-tag--interactive {
  cursor: pointer;
  user-select: none;
  transition: filter 0.12s ease, box-shadow 0.12s ease;
}
.role-tag--interactive:hover {
  filter: brightness(0.96);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}
.role-tag--interactive:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 122, 199, 0.25);
}
.role-tag--interactive:active {
  filter: brightness(0.9);
}

/* ADMIN — sys-admin identity pill: rich gold in Original, purple in New
   (the shared role-identity token, same as the navbar pill). */
.role-tag-admin {
  --role-tag-color: var(--fi-role-admin-line);
  --role-tag-bg: var(--fi-role-admin-bg);
  --role-tag-fg: var(--fi-role-admin-fg);
}
/* MUGGLE — gray, the "I'm pretending to be a regular customer" state. */
.role-tag-muggle {
  --role-tag-color: #8a8f97;
  --role-tag-bg: #e9ecef;
  --role-tag-fg: #4a4f55;
}

/* non-toggle role variants — each picks a colour family that nods to the
   role: blue for customer-admin (settings), neutral for staff, teal for
   booking, green for driver, etc. */
.role-tag-customer-admin {
  --role-tag-color: #1e7eeb;
  --role-tag-bg: #e7f1ff;
  --role-tag-fg: #0b3d91;
}
.role-tag-customer {
  --role-tag-color: #6c757d;
  --role-tag-bg: #eef0f3;
  --role-tag-fg: #3a3f45;
}
.role-tag-booking-customer {
  --role-tag-color: #0aa69a;
  --role-tag-bg: #d9f5f1;
  --role-tag-fg: #0a6e67;
}
.role-tag-driver {
  --role-tag-color: #28a745;
  --role-tag-bg: #e9f6ee;
  --role-tag-fg: #155724;
}
.role-tag-customer-care {
  --role-tag-color: var(--fi-role-care-line);
  --role-tag-bg: var(--fi-role-care-bg);
  --role-tag-fg: var(--fi-role-care-fg);
}
.role-tag-manager {
  --role-tag-color: #3d4fb0;
  --role-tag-bg: #e4e8f8;
  --role-tag-fg: #27336d;
}
.role-tag-dispatch {
  --role-tag-color: #c48b00;
  --role-tag-bg: #fff1d1;
  --role-tag-fg: #6b4b00;
}
.role-tag-garage {
  --role-tag-color: #5a6473;
  --role-tag-bg: #e6e9ee;
  --role-tag-fg: #2d3440;
}
.role-tag-wash-bay {
  --role-tag-color: #17a2b8;
  --role-tag-bg: #dff5f8;
  --role-tag-fg: #0b6373;
}
.role-tag-other {
  --role-tag-color: #6c757d;
  --role-tag-bg: #e9ecef;
  --role-tag-fg: #495057;
}

.ud-actions-cell {
  padding-top: 25px;
  text-align: center;
}

/* tutorial-preferences live preview — each setting has its demo inline
   below its own dropdown. mirrors the thinking-animation preview pattern in
   tenant_config_fleet_assistant. shared soft-gray stage background matches
   the FA settings' .animation-preview visual language. */
.ud-preview-stage {
  width: 100%;
  box-sizing: border-box;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* contain the chase drop-shadow + aurora wash so they clip cleanly at the
     stage's rounded corners rather than bleeding onto the dropdown above
     or the help-text column beside it. the stage reads as a self-contained
     preview "window" with this. */
  overflow: hidden;
  /* inline-size containment stops the browser from asking our children
     what their intrinsic width is when sizing the enclosing <td>. without
     this, the unclipped text span's full width leaks out through table
     auto-layout and widens the column as chars type in. */
  contain: inline-size;
}
.ud-preview-stage--typing {
  min-height: 70px;
  /* padding gives the halo pseudo-elements room (up to ~14px for aurora)
     to extend outside the textbox without clipping at the stage edges. */
  padding: 18px 22px;
}

/* avatar stage — the icon idles at launcher-ish scale; clicking plays its
   attention animation. fa-blue text color so the currentColor variants
   (bubble, cloud) render the same hue as the real launcher pill. */
.ud-preview-stage--avatar {
  min-height: 86px;
  padding: 14px;
}
.ud-avatar-demo-btn {
  border: none;
  background: transparent;
  padding: 6px;
  cursor: pointer;
  color: var(--fi-brand);
  line-height: 0;
  border-radius: 10px;
}
.ud-avatar-demo-btn:hover {
  background: rgba(0, 122, 199, 0.08);
}
.ud-avatar-demo-btn:focus-visible {
  outline: 2px solid #4682B4;
  outline-offset: 2px;
}
.ud-avatar-demo-svg {
  width: 52px;
  height: 52px;
  display: block;
}

/* typing stage — fake input showing the demo message getting typed out.
   matches the real FA input's visual language (rounded, soft border,
   blue focus tint) at a smaller scale. position: relative so the
   highlight-style pseudo-elements anchor to the textbox itself.
   min-width:0 on the input (and on the text span below) keeps the
   box-width stable as the typing span fills in — without overflow:hidden
   on the input itself, which would clip the halo pseudo-elements. */
.ud-preview-input {
  position: relative;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #c9d1d9;
  border-radius: 6px;
  padding: 8px 10px;
  width: 100%;
  min-width: 0;
  min-height: 38px;
  font-size: 13px;
  color: #333;
  line-height: 1.4;
  display: flex;
  align-items: center;
  font-family: inherit;
  font-weight: normal;
}
/* inner wrapper clips overflowing text while keeping the caret inline
   right after the typed span. without this, a flex text span stretched
   to flex:1 pushes the caret to the row's right edge. */
.ud-preview-input-inner {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  display: inline-block;
  line-height: 1;
}
.ud-preview-text {
  white-space: nowrap;
}

/* PULSE — rainbow halo pulsing through 6 hues, 0.8s cycle. duplicated from
   fleet_assistant_launcher.vue because scoped styles don't cross component
   boundaries. if this palette changes there, update here too (or promote
   both to a shared stylesheet). */
.ud-preview-input--pulse {
  animation: ud-preview-halo 0.8s ease infinite;
}
@keyframes ud-preview-halo {
  0%   { box-shadow: 0 0 0 4px rgba(255, 0, 0, 0.55),   0 0 20px 6px rgba(255, 0, 0, 0.35); }
  16%  { box-shadow: 0 0 0 4px rgba(255, 165, 0, 0.55), 0 0 20px 6px rgba(255, 165, 0, 0.35); }
  33%  { box-shadow: 0 0 0 4px rgba(255, 220, 0, 0.55), 0 0 20px 6px rgba(255, 220, 0, 0.35); }
  50%  { box-shadow: 0 0 0 4px rgba(0, 180, 0, 0.55),   0 0 20px 6px rgba(0, 180, 0, 0.35); }
  66%  { box-shadow: 0 0 0 4px rgba(0, 100, 255, 0.55), 0 0 20px 6px rgba(0, 100, 255, 0.35); }
  83%  { box-shadow: 0 0 0 4px rgba(140, 0, 255, 0.55), 0 0 20px 6px rgba(140, 0, 255, 0.35); }
  100% { box-shadow: 0 0 0 4px rgba(255, 0, 0, 0.55),   0 0 20px 6px rgba(255, 0, 0, 0.35); }
}

/* CHASE — same @property sweep technique as the real launcher: the conic
   gradient's `from` angle animates, the pseudo-element stays put. */
@property --ud-chase-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
.ud-preview-input--chase::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: inherit;
  padding: 4px;
  background: repeating-conic-gradient(
    from var(--ud-chase-angle),
    #ff2d6d   0deg,
    #ff5a3a   5deg,
    #ff8a1f  10deg,
    #ffb820  15deg,
    #ffe21f  20deg,
    #8fdd4a  25deg,
    #2bd96d  30deg,
    #2bd3bd  35deg,
    #1e9bff  40deg,
    #6a6dff  45deg,
    #b94dff  50deg,
    #ff3db9  55deg,
    #ff2d6d  60deg
  );
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
          mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  filter: saturate(1.25) drop-shadow(0 0 5px rgba(255, 255, 255, 0.45));
  animation: ud-preview-chase 1.2s linear infinite;
  pointer-events: none;
}
@keyframes ud-preview-chase {
  to { --ud-chase-angle: 60deg; }
}

/* MAGENTA — single-color magenta glow breathing softly. spread + blur
   radii stay constant across keyframes so only the glow intensity
   breathes, not the visible dimensions of the box. */
.ud-preview-input--magenta {
  animation: ud-preview-magenta-breathe 1.4s ease-in-out infinite;
}
@keyframes ud-preview-magenta-breathe {
  0%, 100% {
    box-shadow:
      0 0 0 4px rgba(213, 0, 249, 0.35),
      0 0 22px 6px rgba(213, 0, 249, 0.30);
  }
  50% {
    box-shadow:
      0 0 0 4px rgba(255, 77, 255, 0.70),
      0 0 22px 6px rgba(213, 0, 249, 0.60);
  }
}

/* AURORA — slow drifting green/teal/purple wash with three brighter flare
   peaks embedded along the sweep so the halo reads as organic sheets of
   light with occasional brightenings rather than an even rotation. two
   counter-rotating layers drift at different speeds; each breathes its
   opacity AND its blur independently so the wash shifts between diffuse
   and crisper as the flares move. */
@property --ud-aurora-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
@property --ud-aurora-angle-b {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
.ud-preview-input--aurora::before,
.ud-preview-input--aurora::after {
  content: '';
  position: absolute;
  inset: -11px;
  border-radius: inherit;
  padding: 6px;
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
          mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  pointer-events: none;
}
/* primary layer — slow drift, big bright flare at ~45°, softer peak at ~200°.
   irregular angular spacing (not evenly distributed) so the colors don't feel
   mechanical. */
.ud-preview-input--aurora::before {
  background: conic-gradient(
    from var(--ud-aurora-angle),
    rgba(120, 255, 200, 0.40)    0deg,
    rgba(200, 255, 220, 0.95)   45deg,  /* green flare */
    rgba(140, 230, 220, 0.55)   90deg,
    rgba(100, 220, 240, 0.45)  140deg,
    rgba(160, 180, 255, 0.55)  185deg,
    rgba(220, 180, 255, 0.90)  215deg,  /* magenta flare */
    rgba(210, 140, 255, 0.45)  260deg,
    rgba(140, 220, 200, 0.55)  320deg,
    rgba(120, 255, 200, 0.40)  360deg
  );
  filter: blur(5px);
  animation:
    ud-preview-aurora-drift 7s linear infinite,
    ud-preview-aurora-flare 3.6s ease-in-out infinite alternate;
}
/* secondary layer — counter-rotates faster with its own flare at ~310° so
   the two layers cross each other and never peak in the same place. */
.ud-preview-input--aurora::after {
  background: conic-gradient(
    from var(--ud-aurora-angle-b),
    rgba(140, 220, 200, 0.30)    0deg,
    rgba(180, 240, 220, 0.50)   70deg,
    rgba(160, 200, 255, 0.40)  160deg,
    rgba(200, 160, 240, 0.50)  240deg,
    rgba(240, 200, 255, 0.95)  305deg,  /* violet flare */
    rgba(140, 220, 200, 0.30)  360deg
  );
  filter: blur(7px);
  animation:
    ud-preview-aurora-drift-b 5s linear -2s infinite reverse,
    ud-preview-aurora-flare 2.7s ease-in-out -1.3s infinite alternate-reverse;
}
@keyframes ud-preview-aurora-drift {
  to { --ud-aurora-angle: 360deg; }
}
@keyframes ud-preview-aurora-drift-b {
  to { --ud-aurora-angle-b: 360deg; }
}
/* breath + "flaring" in one — opacity AND blur ease between diffuse/crisp
   so the bright peaks seem to swell and dissolve, giving the halo an
   organic, non-mechanical feel. */
@keyframes ud-preview-aurora-flare {
  0%   { opacity: 0.40; filter: blur(8px); }
  40%  { opacity: 0.85; filter: blur(4px); }
  100% { opacity: 1.00; filter: blur(3px); }
}

/* AURORA V2: the shared slim ring (.fi-halo-aurora-v2 in
   src/assets/css/fi-tutorial-halo.css) around the textbox. the textbox
   never clips (see .ud-preview-input) and the stage's 18px padding leaves
   room for the 7px standoff. knobs match the launcher so the preview shows
   what the tutorial will draw; the radius is the box's 6px corner plus the
   standoff so the ring stays concentric with it. */
.ud-preview-input--aurora-v2 {
  --fi-halo-gap: 3px;
  --fi-halo-width: 4px;
  --fi-halo-blur: 2px;
  --fi-halo-radius: calc(6px + var(--fi-halo-gap) + var(--fi-halo-width));
}

/* RIPPLE — two staggered concentric rings expanding outward. border-width
   stays constant so only the scale + opacity drive the visual, keeping the
   textbox dimensions stable. */
.ud-preview-input--ripple::before,
.ud-preview-input--ripple::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 2px solid rgba(72, 150, 220, 0.7);
  animation: ud-preview-ripple 1.6s ease-out infinite;
  pointer-events: none;
}
.ud-preview-input--ripple::after {
  animation-delay: 0.8s;
}
@keyframes ud-preview-ripple {
  0%   { transform: scale(1);    opacity: 0.75; }
  100% { transform: scale(1.12); opacity: 0; }
}
/* blinking cursor after the typed text. hidden when mode is "off" (no
   animation) so the preview reads as a static dropped-in message. */
.ud-preview-caret {
  display: inline-block;
  width: 1.5px;
  height: 1em;
  background: #666;
  margin-left: 2px;
  animation: ud-preview-blink 1s step-end infinite;
  vertical-align: middle;
}
.ud-preview-caret--off {
  display: none;
}
@keyframes ud-preview-blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

/* narrow viewports: collapse the 3-column table layout into a 2-column grid
   per row. label stays on the left; control stretches across the old
   columns 2+3; help text drops underneath the control (same stretched
   column) so long help strings get room to breathe. each <tr> becomes a
   grid so we can re-flow cells without restructuring the dom. */
/* ::v-deep so the selectors reach the browser-auto-inserted <tbody>, which
   doesn't inherit the component's scoped [data-v-...] attribute. without
   deep, display: block on tbody never applies and the table keeps using
   table-layout, sizing to the widest content and overflowing the viewport
   on narrow screens. */
@media (max-width: 900px) {
  .ud-form-table {
    display: block;
    width: 100%;
    font-size: 16px;
    box-sizing: border-box;
  }
  .ud-form-table ::v-deep tbody {
    display: block;
    width: 100%;
  }
  .ud-form-table ::v-deep tr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    column-gap: 4px;
    width: 100%;
    box-sizing: border-box;
    /* anchor for the absolutely-positioned label cell — keeps a
       multi-line wrapped label from pushing the help text down
       inside its row. mirrors tenant_config_fleet_assistant. */
    position: relative;
  }
  /* breathing room between settings — keeps the next row's label/control
     from butting up against the prior row's help text. */
  .ud-form-table ::v-deep tr + tr {
    margin-top: 10px;
  }
  .ud-form-table ::v-deep tr > td {
    min-width: 0;
    overflow-wrap: break-word;
    /* shrink legacy cellpadding so the input sits close to its help
       text below it when a long label forces the input column to
       stack. without this, each td has 10px top + 10px bottom from
       cellpadding="10" on the table, producing a ~20px vertical gap
       between input and help. */
    padding-top: 3px !important;
    padding-bottom: 3px !important;
  }
  .ud-section-cell,
  .ud-actions-cell {
    grid-column: 1 / -1;
  }
  /* restore breathing room above Save/Cancel — the shared "tr > td
     { padding-top: 3px !important }" rule above wins on specificity
     unless we match its selector depth (class + descendant + tr > td). */
  .ud-form-table ::v-deep tr > td.ud-actions-cell {
    padding-top: 22px !important;
  }
  .ud-label {
    /* absolutely positioned so its height doesn't contribute to the
       grid rows — this keeps the help text (row 2) tight under the
       input (row 1) even when the label wraps to multiple lines.
       mirrors the tenant_config_fleet_assistant pattern. */
    position: absolute;
    left: 0;
    top: 0;
    width: calc((100% / 3) - 4px) !important;
    white-space: normal;
    padding-right: 10px;
  }
  .ud-control {
    grid-column: 2;
    grid-row: 1;
    width: auto;
  }
  .ud-help {
    grid-column: 2;
    grid-row: 2;
    padding-top: 2px;
    width: auto;
  }
  /* form controls fill the stretched column instead of their desktop
     fixed widths. */
  .ud-control .custom-select,
  .ud-control select,
  .ud-control input[type="text"],
  .ud-control input[type="email"],
  .ud-control input[type="password"],
  .ud-control input[type="tel"],
  .ud-control input[type="number"],
  .ud-control textarea,
  .ud-control .form-control {
    width: 100%;
    max-width: 100%;
  }
  .ud-preview-stage {
    max-width: 100%;
  }
}
</style>
